import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { BehaviorSubject, Observable } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import { Product } from 'src/app/domain/Product';
import { Category } from 'src/app/domain/Category';
import { Collection } from 'src/app/domain/Collection';
import { Pack } from 'src/app/domain/Pack';

import { OrderService } from 'src/app/services/order.service';
import { DeleteCustomizationComponent } from '../shared/modals/delete-customization/delete-customization.component';

export interface ProductsResponse {
  products: Product[];
  categories?: Category[];
  collections?: Collection[];
  pages: number;
  total?: number;
  noCategory?: number;
  packs?: Pack[];
}

export interface ProductResponse {
  product: Product;
}

export interface CategoriesResponse {
  categories: Category[];
}
export interface PackResponse {
  pack: Pack;
}

export interface FiltersResponse {
  code: number;
  filters: FiltersClass;
}

export interface FiltersClass {
  measures: Filter[];
  materials: Filter[];
  volumes: Filter[];
  minPrice: number;
  maxPrice: number;
  minDiameter: number;
  maxDiameter: number;
  categories: Filter[];
}

export interface Filter {
  id: number;
  value?: string;
}

export interface CustomerProductsResponse {
  products: Product[];
}

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private readonly http = inject(HttpClient);
  private readonly modalService = inject(NgbModal);
  private readonly orderService = inject(OrderService);

  private readonly categoriesSubject$: BehaviorSubject<Category[]>;
  public categories$: Observable<Category[]>;

  private readonly loadedCategoryLists = new Set<string>();

  constructor() {
    this.categoriesSubject$ = new BehaviorSubject(null);
    this.categories$ = this.categoriesSubject$.asObservable();
  }

  public products(
    page: number,
    items: number,
    categorySlug?: string,
    collectionSlug?: string,
    sectionBuyAgain?: boolean,
    sectionFeaturedProducts?: boolean,
    sort?: string,
    filters?: FiltersClass
  ): Observable<any> {
    return this.http
      .post('product/list', {
        page,
        items,
        categorySlug,
        collectionSlug,
        sectionBuyAgain,
        sectionFeaturedProducts,
        sort,
        filters: this.updateFilters(filters)
      })
      .pipe(tap());
  }

  public product(sku: string): Observable<any> {
    return this.http.post(`product/detail`, { sku }).pipe(tap());
  }

  public productHreflang(sku: string): Observable<any> {
    return this.http.post(`product/detail-hreflang`, { sku }).pipe(
      map((data: any) => {
        return data;
      }),
      tap());
  }

  public categoryHreflang(slug: string): Observable<any> {
    return this.http.get(`product/category/detail-hreflang/${slug}`).pipe(
      map((data: any) => {
        return data;
      }),
      tap());
  }

  public favouriteProducts(page: number, items: number): Observable<any> {
    return this.http.post('product/favourite/list', { page, items }).pipe(
      map((data: ProductsResponse) => {
        return data;
      }),
      tap());
  }

  public search(page: number, items: number, query: string): Observable<any> {
    return this.http.post('product/list-query', { page, items, query }).pipe(
      map((data: ProductsResponse) => {
        return data;
      }),
      tap());
  }

  public categories(): Observable<any> {
    return this.http.get('product/category/list');
  }

  public addFavorite(productId: number): Observable<any> {
    return this.http.patch('product/favourite/add', { productId });
  }

  public removeFavorite(productId: number): Observable<any> {
    return this.http.patch('product/favourite/remove', { productId });
  }

  public filtersCategory(category: string) {
    return this.http.get('product/category/filters/' + category);
  }

  public getCollection(slug: string): Observable<any> {
    return this.http.get(`product/collection/${slug}`).pipe(
      map((data: any) => {
        return data;
      }),
      tap());
  }

  public collectionHreflang(slug: string): Observable<any> {
    return this.http.get(`product/collection/detail-hreflang/${slug}`).pipe(
      map((data: any) => {
        return data;
      }),
      tap());
  }

  public filtersCollection(collection: string) {
    return this.http.get('product/collection/filters/' + collection);
  }

  public updateFilters(filters?: FiltersClass) {
    let updatedFilters: FiltersClass;
    if (filters) {
      updatedFilters = {
        measures: filters.measures,
        materials: filters.materials,
        volumes: filters.volumes,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        minDiameter: filters.minDiameter,
        maxDiameter: filters.maxDiameter,
        categories: filters.categories,
      };
    } else {
      updatedFilters = {
        measures: [],
        materials: [],
        volumes: [],
        minPrice: null,
        maxPrice: null,
        minDiameter: null,
        maxDiameter: null,
        categories: [],
      };
    }
    return updatedFilters;
  }

  public setCategories(countryCode: string) {
    if (!countryCode || this.loadedCategoryLists.has(countryCode)) {
      return;
    }

    this.loadedCategoryLists.add(countryCode);
    this.categories().subscribe(
      (x: CategoriesResponse) => {
        this.categoriesSubject$.next(x.categories);
      }
    );
  }

  public pack(slug: string): Observable<any> {
    return this.http.get(`product/pack/detail/${slug}`).pipe(tap());
  }

  public noStockToBuy(product: Product, securityStock: number): boolean {
    if (!product?.packUnits) {
      return true;
    }

    if (product.isMaster) {
      if (!product.slavesVariants || product.slavesVariants.length === 0) {
        return true;
      }
      return !product.slavesVariants.some(slave =>
        slave.stock >= product.packUnits &&
        slave.stock > securityStock * product.packUnits
      );
    } else if (!product.stock) {
      return true;
    }


    return product.stock < product.packUnits || product.stock <= securityStock * product.packUnits;
  }

  public getProductsBuyAgain(items: number = null): Observable<any> {
      return this.http
        .post('product/buy_again/list', { items })
        .pipe();
    }

  public filtersBuyAgain() {
    return this.http.get('product/buy_again/filters');
  }

  public filtersFeaturedProducts() {
    return this.http.get('product/featured_products/filters');
  }

  public getProductsSelectForYou(): Observable<any> {
    return this.http.get('product/select_for_you/list');
  }

  public getDesign(designId: string, writeToken: string) {
    return this.http
      .post(`product/design/${designId}`, { writeToken })
      .pipe(tap());
  }

  deleteCustomization(product: Product) {
    const modalRef = this.modalService.open(DeleteCustomizationComponent, { windowClass: 'modal-delete-customization' });
    modalRef.result.then((result) => {
      if (result) {
        this.orderService.removeCustomizedFromCart(product);
      }
    });
  }

  getVariantOptionsText(product: Product): string {
    if (!product?.variantOptions?.length) {
      return '';
    }
    return product.variantOptions.map(o => o.name).join(' - ');
  }
}
